import type { AudioClip } from '@shared/types'

/**
 * P10-1 担当C（編集画面プレビューでの BGM 再生）: 純関数群。
 *
 * これらは server/src/render/audio.ts（buildAudioGraph）の filtergraph が
 * 実際に作る音を「タイムライン上のどの秒に、どのくらいの音量で」鳴らすかの
 * プレビュー近似であって、レンダリングそのものの再実装ではない。特に
 * buildAudioGraph の BGM 不足時ロジック（acrossfade ループ・
 * BGM_TRIM_FADEOUT_SEC の自動延長フェードアウト）は再現しない —
 * usePreviewAudio.ts はそのケースを `<audio loop>` 属性による近似（境界の
 * acrossfade は無し）で扱う、という P10_PHASE1.md §4-1 の割り切りに従う。
 */

export interface ClipWindow {
  /** タイムライン上でこの clip が聞こえ始める秒（clip.startSec をクランプしたもの） */
  startSec: number
  /** タイムライン上でこの clip が聞こえ終わる秒（source 末尾 / timeline 末尾でクランプ） */
  endSec: number
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v))
}

/**
 * clip がタイムライン上で実際に聞こえる区間 [startSec, endSec) を返す。
 * - `outSec` が null なら source 末尾（`sourceDurationSec - inSec`）まで。
 * - どちらの場合も `totalSec`（timeline 末尾）でクランプする。
 * - `inSec`/`outSec`/`startSec` の組が不正（区間長が 0 以下、または
 *   startSec が totalSec 以降）なら長さ 0（`startSec === endSec`）を返す —
 *   呼び出し側の `gainAt` はこれを「常に範囲外」として扱う。
 */
export function clipWindow(clip: AudioClip, sourceDurationSec: number, totalSec: number): ClipWindow {
  const startSec = Math.max(0, clip.startSec)
  if (totalSec <= startSec) return { startSec, endSec: startSec }

  const rawLen =
    clip.outSec != null ? Math.max(0, clip.outSec - clip.inSec) : Math.max(0, sourceDurationSec - clip.inSec)
  if (rawLen <= 0) return { startSec, endSec: startSec }

  const endSec = Math.min(startSec + rawLen, totalSec)
  return { startSec, endSec: Math.max(startSec, endSec) }
}

/**
 * タイムライン上の秒 `timelineSec` に対応する、clip のソース内の秒。
 * `render/audio.ts` の `atrim=start=inSec` + `adelay=startSec` と同じ写像
 * （speed は AudioClip に存在しないので等速）。window の外かどうかは
 * 呼び出し側（`gainAt`）が別途判定する — この関数自体は常に値を返す。
 *
 * reviewer 差し戻し C-1 (2026-09-06): `loopLenSec` 省略時はこれまで通り
 * `inSec + (timelineSec - startSec)` を単調増加のまま返す。`usePreviewAudio.ts`
 * が `<audio loop>` を付けた clip（BGM が尺より短く、不足分をネイティブ
 * loop 属性で埋めている近似 — `shouldLoop`/audioMath.ts 冒頭の doc 参照）に
 * この値をそのまま使うと、source の実尺を超えて増え続ける一方で `<audio>`
 * 自身は `duration` に達するたびに currentTime=0 へ戻る（ネイティブ loop の
 * 挙動）ため、両者の差が毎 tick 閾値を超えて `el.currentTime = expected` が
 * 末尾へ強制シークし続け、ループのたびに「末尾に固定される」形で音が止まる
 * （reviewer 実測: 5s 中 173 回の強制シーク）。
 *
 * `loopLenSec`（= 呼び出し側が計算する `sourceDurationSec - clip.inSec`。
 * 有限かつ 0 より大きい時だけ渡す）を渡すと、`inSec` を起点に `loopLenSec`
 * 周期で折り返した秒を返す — ネイティブ loop が `duration` に達するたびに
 * 0 に戻る挙動と（`inSec` が 0 の典型ケースでは）同じ周期で追従するので、
 * 強制シークは「境界のズレ」を補正する時だけ（1周期あたり高々数回）に減る。
 */
export function sourceTimeAt(clip: AudioClip, timelineSec: number, loopLenSec?: number): number {
  const offset = timelineSec - clip.startSec
  if (loopLenSec != null && Number.isFinite(loopLenSec) && loopLenSec > 0) {
    // JS の `%` は負値に対して負値を返しうる（offset < 0 の時）ので、
    // 正の modulo に正規化してから inSec に足す。
    const wrapped = ((offset % loopLenSec) + loopLenSec) % loopLenSec
    return clip.inSec + wrapped
  }
  return clip.inSec + offset
}

/**
 * `timelineSec` 時点でこの clip にかけるべき `<audio>.volume`（0..1）。
 * `10^(gainDb/20)`（server の `volume=${gainDb}dB` と同じ dB->linear 換算）に
 * フェード包絡を掛ける。包絡は `render/audio.ts` の `afade`（既定 curve=tri
 * ＝線形）と同じ形: `fadeInSec` で window 先頭から 0->1 へ、`fadeOutSec` で
 * window 末尾へ向けて 1->0 へ（両方掛かる区間は min を取る＝ffmpeg が
 * afade=in ->afade=out を直列適用するのと同じ重なり方）。
 * window の外、または window の長さが 0（不正 clip）なら常に 0。
 * 1 を超える場合（gainDb > 0dB や dB->linear の丸め）は 1 にクランプする
 * （`<audio>.volume` の仕様上限が 1 のため）。
 */
export function gainAt(clip: AudioClip, timelineSec: number, window: ClipWindow): number {
  if (timelineSec < window.startSec || timelineSec >= window.endSec) return 0

  const localDur = window.endSec - window.startSec
  const localT = timelineSec - window.startSec

  let envelope = 1
  if (clip.fadeInSec > 0) {
    envelope = Math.min(envelope, clamp01(localT / clip.fadeInSec))
  }
  if (clip.fadeOutSec > 0) {
    const remaining = localDur - localT
    envelope = Math.min(envelope, clamp01(remaining / clip.fadeOutSec))
  }

  const linearGain = Math.pow(10, clip.gainDb / 20)
  return clamp01(linearGain * envelope)
}
